import React, { useState } from 'react';
import {
    IconContainer,
    Learn3XLink,
    LoyaltyProgressSubContainer,
    ProgressBarCenter,
    RewardProgressTotalButtonWithIcon,
    RewardProgressTotalContainer,
    StyledCard,
    TooltipStyles,
} from './CircularProgressBar.styled';
import {
    RewardProgressBarProps,
    PopupTextParams,
    CenterContentParams,
    InfoItemParams,
    TotalSummaryParams,
    TotalButtonParams,
} from './types';
import RadialSeparators from './RadialSeparator';
import SubscriptionVoucherIcon from '../Icons/SubscriptionVoucherIcon';
import SubscriptionVoucherIconGrey from '../Icons/SubscriptionVoucherIconGrey';
import { FaExclamationTriangle } from 'react-icons/fa';
import { NsTypography } from '@nuskin/foundation-ui-components';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import Info from '../Icons/Info';
import { Tooltip, IconButton } from '@mui/material';
import { replacePlaceholders } from '../Utils/helper';

function resolvePopupText({
    voucherValue,
    minVoucher,
    maxVoucher,
    notQualifyForRewardThisMonth,
    learn3xMore,
    learnMoreText,
    translationLearnMore,
}: PopupTextParams) {
    if (voucherValue === minVoucher && !notQualifyForRewardThisMonth) {
        return learn3xMore;
    }
    if (voucherValue === maxVoucher || notQualifyForRewardThisMonth) {
        return learnMoreText;
    }
    if (voucherValue === null || voucherValue === undefined) {
        return translationLearnMore;
    }
    return translationLearnMore;
}

function renderCenterContent({
    isComplete,
    notQualifyForRewardThisMonth,
    hasVoucherValue,
    voucherEarned,
    voucherTargetAmount,
}: CenterContentParams) {
    if (isComplete && !notQualifyForRewardThisMonth) {
        return <SubscriptionVoucherIcon />;
    }
    if (notQualifyForRewardThisMonth) {
        return (
            <IconContainer>
                <FaExclamationTriangle className="outline-exclamation-icon" color="#91ACC8" />
            </IconContainer>
        );
    }
    if (hasVoucherValue) {
        return (
            <ProgressBarCenter>
                <NsTypography
                    component="div"
                    className="center-value-progress-bar-price"
                    variant="title-l"
                    weight="bold"
                >
                    {voucherEarned}
                </NsTypography>
                <NsTypography
                    variant="label-s"
                    weight="bold"
                    component="div"
                    className="center-value-progress-bar-voucher"
                >
                    {voucherTargetAmount}
                </NsTypography>
            </ProgressBarCenter>
        );
    }
    return (
        <ProgressBarCenter>
            <SubscriptionVoucherIconGrey />
        </ProgressBarCenter>
    );
}

function renderInfoItem({ isComplete, amountShow, minAmount, voucherEarnedText, monthsCompleteText }: InfoItemParams) {
    return (
        <div className="info-item">
            {isComplete && amountShow !== undefined && amountShow > minAmount ? voucherEarnedText : monthsCompleteText}
        </div>
    );
}

function renderTotalSummary({
    isComplete,
    notQualifyForRewardThisMonth,
    hasVoucherValue,
    approximateTotalText,
    amountShowText,
    currentlyTrackText,
    callToActionText,
}: TotalSummaryParams) {
    if (isComplete) {
        return null;
    }
    if (notQualifyForRewardThisMonth) {
        return (
            <NsTypography
                variant="body-s"
                weight="normal"
                className="reward-subtitle"
                colorOverride="#252525"
                component="div"
            >
                {approximateTotalText}{' '}
                <NsTypography component="span" className="button-price" variant="body-s" weight="bold">
                    {amountShowText}
                </NsTypography>
            </NsTypography>
        );
    }
    if (hasVoucherValue) {
        return (
            <NsTypography variant="body-s" weight="bold" className="reward-subtitle" colorOverride="#252525">
                {currentlyTrackText}
            </NsTypography>
        );
    }
    return (
        <NsTypography variant="body-s" weight="bold" className="reward-subtitle" colorOverride="#252525">
            {callToActionText}
        </NsTypography>
    );
}

function renderTotalButton({
    notQualifyForRewardThisMonth,
    approximateMonthlyText,
    amountShowText,
    translationNotQualify,
    estimateDisclaimer,
    open,
    handleTooltipToggle,
    handleTooltipClose,
}: TotalButtonParams) {
    if (notQualifyForRewardThisMonth) {
        return (
            <>
                <IconContainer>
                    <FaExclamationTriangle className="loyalty-warning-icon" color="#4A6987" />
                </IconContainer>
                <NsTypography
                    variant="body-s"
                    weight="normal"
                    className="reward-subtitle"
                    colorOverride="#293A4A"
                    noSpacing
                >
                    {translationNotQualify}
                </NsTypography>
            </>
        );
    }

    return (
        <>
            <NsTypography
                variant="body-s"
                weight="normal"
                colorOverride="#252525"
                className="reward-subtitle"
                component="span"
                noSpacing
            >
                {approximateMonthlyText}{' '}
            </NsTypography>
            &nbsp;
            <NsTypography weight="bold" className="button-price" variant="body-s" component="span">
                {amountShowText}
            </NsTypography>
            <IconContainer>
                <Tooltip
                    slotProps={TooltipStyles}
                    title={estimateDisclaimer}
                    arrow
                    placement="top"
                    open={open}
                    onClose={handleTooltipClose}
                >
                    <span className="info-icon">
                        <IconButton onClick={handleTooltipToggle}>
                            <Info />
                        </IconButton>
                    </span>
                </Tooltip>
            </IconContainer>
        </>
    );
}

const SubscriptionRewardMobile = (props: RewardProgressBarProps) => {
    const [open, setOpen] = useState(false);
    const handleTooltipToggle = () => {
        setOpen(!open);
    };
    const handleTooltipClose = () => setOpen(false);
    const {
        handleOpenSubscriptionRewardPopup = () => {},
        notQualifyForRewardThisMonth = false,
        value = 0,
        voucherValue = 0,
        amountShow,
        maxMonths = 0,
        translationConfig = {
            voucherTargetAmount: '',
            monthsComplete: '',
            voucherEarned: '',
            learnMore: '',
            learn3xMore: '',
            callToAction: '',
            notQualify: '',
            approximateMonthly: '',
            approximateTotal: '',
            currentlyTrack: '',
            loyaltyLogoBlack: '',
            estimateDisclaimer: '',
        },
        learnMoreConfig = {
            tagLine: '',
            cardOneDescription: '',
            cardOneVoucherAmount: '',
            cardTwoDescription: '',
            cardTwoVoucherAmount: '',
            header: '',
            loyaltyLogoWhite: '',
            loyaltyBanner: '',
            learnMore: '',
        },
        storeConfig = {
            tiers: [
                {
                    consecutive_months: '',
                    min_spend: '',
                    max_spend: '',
                    promo_code: '',
                },
                {
                    consecutive_months: '',
                    min_spend: '',
                    promo_code: '',
                },
            ],
        },
        currencyAbbreviate = false,
        currencyFormat,
    } = props;
    const minVoucher = 50;
    const maxVoucher = 150;

    const formatCurrency = (price: number, withoutDecimal: boolean, abbreviate: boolean): string => {
        const formatted = currencyFormat?.(price, withoutDecimal, abbreviate);
        return (formatted as unknown as string) ?? '';
    };

    const monthsCompleteText = replacePlaceholders(translationConfig.monthsComplete, {
        completed_months: value,
        total_months: maxMonths,
    });
    const voucherEarned = voucherValue
        ? formatCurrency(voucherValue, true, currencyAbbreviate)
        : formatCurrency(0, true, currencyAbbreviate);
    const voucherEarnedText = translationConfig.voucherEarned.replace(
        '%currency_symbol%%currency_amount%',
        `${voucherEarned}`,
    );
    const currentlyTrackText = translationConfig.currentlyTrack.replace(
        '%currency_symbol%%currency_amount%',
        `${voucherEarned}`,
    );
    const approximateTotalText = replacePlaceholders(translationConfig.approximateTotal, {
        currency_symbol: '',
        projected_month_spend: '',
    });
    const minAmount = parseFloat(String(storeConfig?.tiers?.[0]?.min_spend)) || 0;
    const minAmountShow = formatCurrency(minAmount, true, currencyAbbreviate);
    const callToActionText = translationConfig.callToAction.replace(
        '%currency_symbol%%current_spend_target%',
        `${minAmountShow}`,
    );
    const approximateMonthlyText = replacePlaceholders(translationConfig.approximateMonthly, {
        currency_symbol: '',
        estimated_running_subscription_spend: '',
    });
    const voucherTargetAmount = replacePlaceholders(translationConfig.voucherTargetAmount, {
        currency_symbol: '',
        voucher_amount: '',
    });
    const amountShowText = formatCurrency(amountShow ?? 0, false, false);
    const isComplete = value === maxMonths;
    const hasVoucherValue = voucherValue != null;
    const backgroundColor = value === maxMonths && !notQualifyForRewardThisMonth ? 'rgb(195 240 194)' : '#fff';
    const popupText = resolvePopupText({
        voucherValue,
        minVoucher,
        maxVoucher,
        notQualifyForRewardThisMonth,
        learn3xMore: translationConfig.learn3xMore,
        learnMoreText: learnMoreConfig.learnMore,
        translationLearnMore: translationConfig.learnMore,
    });
    const centerContent = renderCenterContent({
        isComplete,
        notQualifyForRewardThisMonth,
        hasVoucherValue,
        voucherEarned,
        voucherTargetAmount,
    });
    const infoItem = renderInfoItem({
        isComplete,
        amountShow,
        minAmount,
        voucherEarnedText,
        monthsCompleteText,
    });
    const totalSummary = renderTotalSummary({
        isComplete,
        notQualifyForRewardThisMonth,
        hasVoucherValue,
        approximateTotalText,
        amountShowText,
        currentlyTrackText,
        callToActionText,
    });
    const totalButton = renderTotalButton({
        notQualifyForRewardThisMonth,
        approximateMonthlyText,
        amountShowText,
        translationNotQualify: translationConfig.notQualify,
        estimateDisclaimer: translationConfig.estimateDisclaimer ?? '',
        open,
        handleTooltipToggle,
        handleTooltipClose,
    });

    return (
        <LoyaltyProgressSubContainer>
            <StyledCard>
                <img className="reward-logo" src={translationConfig.loyaltyLogoBlack} alt="Rewards Logo" />
            </StyledCard>
            <div className="reward-progress">
                <CircularProgressbarWithChildren
                    value={(value / maxMonths) * 100}
                    strokeWidth={12}
                    background
                    styles={buildStyles({
                        rotation: 0.5,
                        strokeLinecap: 'butt',
                        backgroundColor,
                        pathColor: '#6BC56A',
                        trailColor: '#E0E0E0',
                    })}
                >
                    <RadialSeparators count={maxMonths} />
                    {centerContent}
                </CircularProgressbarWithChildren>
            </div>
            <StyledCard>{infoItem}</StyledCard>
            <RewardProgressTotalContainer>
                {totalSummary}
                <RewardProgressTotalButtonWithIcon>{totalButton}</RewardProgressTotalButtonWithIcon>
            </RewardProgressTotalContainer>
            <StyledCard>
                <Learn3XLink onClick={handleOpenSubscriptionRewardPopup}>
                    <u>{popupText}</u>
                </Learn3XLink>
            </StyledCard>
        </LoyaltyProgressSubContainer>
    );
};

export default SubscriptionRewardMobile;
export { SubscriptionRewardMobile };
